'use client';

import React, { useRef, useState, useEffect } from 'react';
import { useWebAudioTouchUnlock } from '@/hooks/useWebAudioTouchUnlock';

interface TemplateVideoPlayerProps {
  previewVideo: string;
  previewImage?: string;
}

export default function TemplateVideoPlayer({
  previewVideo,
  previewImage,
}: TemplateVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useWebAudioTouchUnlock();

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observerOptions = {
      root: null,
      rootMargin: '700px 0px', // Adjust this value as needed
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(videoElement);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );
    observer.observe(videoElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handlePlay = () => {
    if (!videoRef.current) return;
    videoRef.current.play();
  };
  const handleStop = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  return (
    <div onMouseEnter={handlePlay} onMouseLeave={handleStop}>
      <video
        ref={videoRef}
        src={isVisible ? previewVideo : undefined}
        preload="metadata"
        muted
        loop
        playsInline
        className="rounded-xl"
        style={{
          width: '100%',
          height: '100%',
          aspectRatio: '1/1',
        }}
        poster={previewImage || undefined} // Conditionally add the poster attribute
      />
    </div>
  );
}